import React, { useCallback, useState } from 'react';

import { DeleteModal } from '../DeleteModal';

import { ConfirmModalContext } from './useAsyncConfirm';

import type { AsyncConfirmFn, ConfirmOptions, ConfirmResult } from './useAsyncConfirm';
import type { ChildrenMetaFunction } from '../DeleteModal';

type ConfirmProviderProps = {
	children: React.ReactNode;
};

type InternalState<T = unknown> = ConfirmOptions & {
	isOpen: boolean;
	resolve?: (value: ConfirmResult<T>) => void;
	nodeFn?: ChildrenMetaFunction<T>;
	defaultMeta?: T;
	isMetaValid?: (meta: T | undefined) => boolean;
};

export const ConfirmModalProvider: React.FC<ConfirmProviderProps> = <T = unknown,>({
	children,
}: ConfirmProviderProps) => {
	const [state, setState] = useState<InternalState<T>>({ isOpen: false });
	const confirm: AsyncConfirmFn<T> = useCallback(
		(
			options: ConfirmOptions,
			nodeFn?: ChildrenMetaFunction<T>,
			defaultMeta?: T,
			isMetaValid?: (meta: T | undefined) => boolean
		) => {
			return new Promise<ConfirmResult<T>>((resolve) => {
				setState({
					...options,
					isOpen: true,
					nodeFn,
					resolve,
					defaultMeta: defaultMeta,
					isMetaValid,
				});
			});
		},
		[]
	);

	const handleResolveState = (result: boolean, meta?: T) => {
		state.resolve?.([result, meta] as ConfirmResult<T>);

		setState((prev) => ({
			...prev,
			isOpen: false,
			resolve: undefined,
		}));
	};

	return (
		<ConfirmModalContext.Provider value={{ confirm } as { confirm: AsyncConfirmFn }}>
			{children}
			{state.isOpen && (
				<DeleteModal<T>
					isDisabled={(e) => (state.isMetaValid ? !state.isMetaValid(e) : false)}
					defaultMeta={state.defaultMeta}
					title={state.title ?? ''}
					confirmText={state.confirmText ?? 'Confirm'}
					cancelText={state.cancelText ?? 'Cancel'}
					onCloseRequest={() => {
						handleResolveState(false, undefined);
					}}
					onClick={(metaResponse) => {
						handleResolveState(true, metaResponse);
					}}
				>
					{({ meta, setMeta }) => {
						return state.nodeFn ? state.nodeFn({ meta, setMeta }) : state.message;
					}}
				</DeleteModal>
			)}
		</ConfirmModalContext.Provider>
	);
};
